import React, { useState, useEffect, useRef } from "react";
import PostulacionForm from "./PostulacionForm";
import {
  Dialog,
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Slide,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";

const SESSION_KEY = "recruitment_popup_shown";

// ── Partículas flotantes decorativas ──────────────────────────────────────────
const Particle = ({ style }) => (
  <Box
    sx={{
      position: "absolute",
      borderRadius: "50%",
      pointerEvents: "none",
      animation: "floatParticle 6s ease-in-out infinite",
      ...style,
    }}
  />
);

// ── Orbe de luz de fondo ───────────────────────────────────────────────────────
const LightOrb = ({ sx }) => (
  <Box
    sx={{
      position: "absolute",
      borderRadius: "50%",
      filter: "blur(80px)",
      pointerEvents: "none",
      ...sx,
    }}
  />
);

const FEATURES = [
  { emoji: "🎉", label: "Ambiente dinámico" },
  { emoji: "📸", label: "Eventos increíbles" },
  { emoji: "🚀", label: "Crecimiento real" },
  { emoji: "🤝", label: "Equipo que inspira" },
];

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const RecruitmentPopup = () => {
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState(0); // 0=loading, 1=ready
  const [showForm, setShowForm] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (sessionStorage.getItem(SESSION_KEY)) return;

    // Verificar si la campaña está activa antes de mostrar el popup
    fetch("/api/configuracion?clave=reclutamiento_activo")
      .then((res) => res.json())
      .then((data) => {
        const activo = data?.valor === true || data?.valor === 1 || data?.valor === "1";
        if (!activo) return; // campaña pausada → no mostrar
        timerRef.current = setTimeout(() => {
          setPhase(1);
          setOpen(true);
          sessionStorage.setItem(SESSION_KEY, "1");
        }, 800);
      })
      .catch(() => {
        // Si falla la consulta, mostrar por defecto
        timerRef.current = setTimeout(() => {
          setPhase(1);
          setOpen(true);
          sessionStorage.setItem(SESSION_KEY, "1");
        }, 800);
      });

    return () => clearTimeout(timerRef.current);
  }, []);

  const handleClose = () => setOpen(false);
  const handleOpenForm = () => {
    setOpen(false);
    setShowForm(true);
  };

  if (phase === 0) return null;

  return (
    <>
      {/* ── Keyframes globales ── */}
      <style>{`
        @keyframes floatParticle {
          0%, 100% { transform: translateY(0px) scale(1); opacity: 0.6; }
          50%       { transform: translateY(-20px) scale(1.15); opacity: 1; }
        }
        @keyframes shimmer {
          0%   { background-position: -400px 0; }
          100% { background-position: 400px 0; }
        }
        @keyframes pulseRing {
          0%   { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(0, 201, 255, 0.5); }
          70%  { transform: scale(1); box-shadow: 0 0 0 14px rgba(0, 201, 255, 0); }
          100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(0, 201, 255, 0); }
        }
        @keyframes gradientFlow {
          0%   { background-position: 0% 50%; }
          50%  { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes textGlow {
          0%, 100% { text-shadow: 0 0 20px rgba(0,201,255,0.4); }
          50%       { text-shadow: 0 0 40px rgba(146,254,157,0.6); }
        }
      `}</style>

      <Dialog
        open={open}
        onClose={handleClose}
        TransitionComponent={Transition}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: "transparent",
            boxShadow: "none",
            overflow: "visible",
            m: { xs: 1.5, sm: 3 },
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              background: "rgba(4, 6, 20, 0.88)",
              backdropFilter: "blur(12px)",
            },
          },
        }}
      >
        {/* ── Tarjeta principal ── */}
        <Box
          sx={{
            position: "relative",
            borderRadius: "24px",
            overflow: "hidden",
            background: "linear-gradient(145deg, #090d1f 0%, #0f1630 50%, #0a1020 100%)",
            border: "1px solid rgba(0, 201, 255, 0.2)",
            boxShadow:
              "0 0 0 1px rgba(146,254,157,0.08), 0 32px 80px rgba(0,0,0,0.7), 0 0 60px rgba(0,201,255,0.12)",
          }}
        >
          {/* ── Orbes de luz ── */}
          <LightOrb sx={{ width: 300, height: 300, background: "rgba(0,201,255,0.12)", top: -100, left: -80 }} />
          <LightOrb sx={{ width: 250, height: 250, background: "rgba(146,254,157,0.1)", bottom: -80, right: -60 }} />
          <LightOrb sx={{ width: 180, height: 180, background: "rgba(130,80,255,0.08)", top: "40%", left: "50%", transform: "translate(-50%,-50%)" }} />

          {/* ── Partículas flotantes ── */}
          <Particle style={{ width: 8, height: 8, background: "#00C9FF", top: "12%", left: "8%", animationDelay: "0s" }} />
          <Particle style={{ width: 5, height: 5, background: "#92FE9D", top: "20%", right: "12%", animationDelay: "1.5s" }} />
          <Particle style={{ width: 6, height: 6, background: "#92FE9D", bottom: "25%", left: "15%", animationDelay: "3s" }} />
          <Particle style={{ width: 4, height: 4, background: "#00C9FF", bottom: "15%", right: "20%", animationDelay: "0.8s" }} />
          <Particle style={{ width: 7, height: 7, background: "rgba(146,254,157,0.7)", top: "60%", left: "5%", animationDelay: "2.2s" }} />

          {/* ── Barra superior degradada animada ── */}
          <Box
            sx={{
              height: 5,
              background: "linear-gradient(90deg, #00C9FF, #92FE9D, #8250ff, #00C9FF)",
              backgroundSize: "300% 100%",
              animation: "gradientFlow 4s linear infinite",
            }}
          />

          {/* ── Botón cerrar ── */}
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              zIndex: 10,
              color: "rgba(255,255,255,0.5)",
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.1)",
              backdropFilter: "blur(8px)",
              transition: "all 0.2s",
              "&:hover": {
                color: "#fff",
                background: "rgba(255,255,255,0.12)",
                border: "1px solid rgba(255,255,255,0.2)",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          {/* ── Contenido ── */}
          <Box sx={{ p: { xs: 3, sm: 4.5 }, pt: { xs: 3, sm: 3.5 } }}>

            {/* Logo / marca */}
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 72,
                  height: 72,
                  borderRadius: "22px",
                  background: "linear-gradient(135deg, rgba(0,201,255,0.15) 0%, rgba(146,254,157,0.1) 100%)",
                  border: "1.5px solid rgba(0,201,255,0.3)",
                  mb: 2,
                  animation: "pulseRing 2.5s ease-in-out infinite",
                  fontSize: 34,
                }}
              >
                🎥
              </Box>

              <Chip
                label="ESTAMOS CONTRATANDO"
                size="small"
                sx={{
                  mb: 2,
                  background: "linear-gradient(90deg, rgba(0,201,255,0.15), rgba(146,254,157,0.15))",
                  border: "1px solid rgba(0,201,255,0.35)",
                  color: "#00C9FF",
                  fontWeight: 700,
                  letterSpacing: "0.12em",
                  fontSize: "0.62rem",
                  height: 24,
                }}
              />

              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: "1.5rem", sm: "1.85rem" },
                  lineHeight: 1.2,
                  mb: 1,
                  background: "linear-gradient(135deg, #00C9FF 0%, #92FE9D 60%, #fff 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  animation: "textGlow 3s ease-in-out infinite",
                }}
              >
                Forma parte de<br />A La Vuelta 360°
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: "rgba(255,255,255,0.5)",
                  fontSize: "0.78rem",
                  letterSpacing: "0.06em",
                  fontStyle: "italic",
                }}
              >
                Creamos momentos que las personas recuerdan para siempre ✨
              </Typography>
            </Box>

            {/* ── Separador ── */}
            <Box
              sx={{
                height: "1px",
                background: "linear-gradient(90deg, transparent, rgba(0,201,255,0.3), rgba(146,254,157,0.3), transparent)",
                mb: 3,
              }}
            />

            {/* Descripción */}
            <Typography
              variant="body2"
              sx={{
                color: "rgba(255,255,255,0.75)",
                lineHeight: 1.75,
                fontSize: { xs: "0.84rem", sm: "0.88rem" },
                textAlign: "center",
                mb: 3,
              }}
            >
              Buscamos personas con{" "}
              <Box component="span" sx={{ color: "#92FE9D", fontWeight: 600 }}>energía, actitud y pasión</Box>{" "}
              por los eventos, la atención al cliente y la creatividad.
              <br />
              Si te gusta trabajar en equipo, conectar con personas y ser parte de experiencias únicas,{" "}
              <Box component="span" sx={{ color: "#00C9FF", fontWeight: 600 }}>este es tu lugar.</Box>
            </Typography>

            {/* ── Feature chips ── */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 1.5,
                mb: 3.5,
              }}
            >
              {FEATURES.map(({ emoji, label }) => (
                <Box
                  key={label}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.2,
                    p: 1.5,
                    borderRadius: "14px",
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    backdropFilter: "blur(8px)",
                    transition: "all 0.25s",
                    cursor: "default",
                    "&:hover": {
                      background: "rgba(0,201,255,0.07)",
                      border: "1px solid rgba(0,201,255,0.2)",
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  <Box sx={{ fontSize: "1.3rem", lineHeight: 1 }}>{emoji}</Box>
                  <Typography
                    variant="caption"
                    sx={{ color: "rgba(255,255,255,0.8)", fontWeight: 600, fontSize: "0.78rem" }}
                  >
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Texto cierre */}
            <Typography
              variant="body2"
              sx={{
                color: "rgba(255,255,255,0.6)",
                fontSize: "0.82rem",
                textAlign: "center",
                mb: 3,
                lineHeight: 1.6,
              }}
            >
              Únete a una marca que está{" "}
              <Box component="span" sx={{ color: "#92FE9D", fontWeight: 600 }}>
                transformando la manera de vivir los eventos.
              </Box>
            </Typography>

            {/* ── CTA Button ── */}
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleOpenForm}
              sx={{
                py: 1.6,
                borderRadius: "14px",
                fontWeight: 800,
                fontSize: "0.95rem",
                letterSpacing: "0.03em",
                background: "linear-gradient(90deg, #00C9FF 0%, #92FE9D 100%)",
                backgroundSize: "200% 100%",
                color: "#060d1a",
                textTransform: "none",
                boxShadow: "0 8px 32px rgba(0,201,255,0.35), 0 4px 12px rgba(0,0,0,0.3)",
                transition: "all 0.3s cubic-bezier(0.34,1.56,0.64,1)",
                "&:hover": {
                  background: "linear-gradient(90deg, #92FE9D 0%, #00C9FF 100%)",
                  transform: "translateY(-2px) scale(1.01)",
                  boxShadow: "0 12px 40px rgba(0,201,255,0.5), 0 6px 16px rgba(0,0,0,0.4)",
                },
                "&:active": {
                  transform: "translateY(0px) scale(0.99)",
                },
              }}
            >
              🚀 ¿Listo para ser parte de la experiencia? ¡Postúlate aquí!
            </Button>

            {/* Texto bajo CTA */}
            <Typography
              variant="caption"
              sx={{
                display: "block",
                textAlign: "center",
                color: "rgba(255,255,255,0.3)",
                mt: 2,
                fontSize: "0.7rem",
              }}
            >
              © {new Date().getFullYear()} A La Vuelta 360° · Todos los derechos reservados
            </Typography>
          </Box>
        </Box>
      </Dialog>

      <PostulacionForm open={showForm} onClose={() => setShowForm(false)} />
    </>
  );
};

export default RecruitmentPopup;
